import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import React from "react";

import {
	handlePublishPromise,
	persistWarningLevel,
} from "../mqtt-client/publishers";
import {
	configurationStore,
	cwpStore,
	distanceLineStore,
	trajectoryPredictionStore,
} from "../state";
import SpeedVectorNavbarControl from "./SpeedVectorNavbarControl";
import Time from "./Time";

const _ControllerButton = observer(function ControllerButton() {
	const { currentCWP } = configurationStore;
	return (
		<button
			type="button"
			onClick={(): void => cwpStore.toggleControllerSelection()}
			className="h-full text-white text-xs bg-[#1e3a5f] rounded-none border border-[#2a5d8f] overflow-hidden whitespace-nowrap shrink hover:bg-[#2a5d8f] hover:border-[#4b90db] active:bg-[#366fa3] active:border-[#5aa1e6] focus:outline-none focus:shadow-none focus:border-[#3f77b2]"
		>
			{currentCWP || "Controller"}
		</button>
	);
});

function GenericButton({
	children,
	onClick,
	active,
}: {
	children: React.ReactNode;
	onClick: () => void;
	active: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={classNames(
				"h-full text-white text-xs rounded-none overflow-hidden whitespace-nowrap shrink",
				"focus:outline-none focus:shadow-none p-1.5 pt-1 pb-1",
				active
					? "bg-[#4b90db] hover:bg-[#5898dd] active:bg-[#4b90db]"
					: "bg-[#1e3a5f] hover:bg-[#25436b] active:bg-[#1e3a5f]",
			)}
		>
			{children}
		</button>
	);
}

const _FLButton = observer(function FLButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleFL()}
			active={cwpStore.showFL}
		>
			FL
		</GenericButton>
	);
});

const _NSFLButton = observer(function NSFLButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleFL()} // Not correct but might be useful?
			active={cwpStore.showFL}
		>
			NSFL
		</GenericButton>
	);
});

const _SFLButton = observer(function SFLButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleSFL()}
			active={cwpStore.showSFL}
		>
			SFL
		</GenericButton>
	);
});

const _N_AButton = observer(function N_AButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleSFL()}
			active={cwpStore.showSFL} // Not correct but might be useful?
		>
			N_A
		</GenericButton>
	);
});

const SectorLabelsButton = observer(function SectorLabelsButton() {
	const posthog = usePostHog();
	const onClick = (): void => {
		cwpStore.toggleSectorLabels();
		posthog?.capture("sectors_toggled", {
			enabled: !cwpStore.showSectorLabels,
		});
	};
	return (
		<GenericButton
			onClick={onClick}
			active={cwpStore.showSectorLabels} // Not sure if this is correct but might be useful?
		>
			Sectors
		</GenericButton>
	);
});

const AirwaysButton = observer(function AirwaysButton() {
	const posthog = usePostHog();
	const onClick = (): void => {
		cwpStore.toggleAirways();
		posthog?.capture("airways_toggled", {
			enabled: !cwpStore.showAirways,
		});
	};
	return (
		<GenericButton onClick={onClick} active={cwpStore.showAirways}>
			Airways
		</GenericButton>
	);
});

const ResetButton = observer(function ResetButton() {
	const posthog = usePostHog();
	const resetAll = (): void => {
		// Clear retained MQTT messages for all active warning levels before resetting
		const role = configurationStore.currentCWP;
		for (const aircraftId of cwpStore.aircraftWarningLevels.keys()) {
			handlePublishPromise(persistWarningLevel(aircraftId, "none", role));
		}

		// Reset per-aircraft UI state (selections, warnings, popups)
		cwpStore.resetUIState();

		// Reset distance measurement markers
		distanceLineStore.reset();

		// Reset trajectory prediction state
		trajectoryPredictionStore.reset();

		posthog?.capture("ui_reset", {
			full_reset: true,
		});
	};
	return (
		<GenericButton onClick={(): void => resetAll()} active={true}>
			RESET
		</GenericButton>
	);
});

const _FlightLabelsButton = observer(function FlightLabelsButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleFlightLabels()}
			active={cwpStore.showFlightLabels}
		>
			Flight Labels
		</GenericButton>
	);
});

const FixesButton = observer(function FixesButton() {
	const posthog = usePostHog();
	const onClick = (): void => {
		cwpStore.toggleFixes();
		posthog?.capture("fixes_toggled", {
			enabled: !cwpStore.showFixes,
		});
	};
	return (
		<GenericButton onClick={onClick} active={cwpStore.showFixes}>
			&#x25B2;
		</GenericButton>
	);
});

const _LimboFlightsButton = observer(function LimboFlightsButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleLimboFlights()}
			active={cwpStore.showLimboFlight}
		>
			Affected Flights
		</GenericButton>
	);
});

const _FilterButton = observer(function FilterButton() {
	return (
		<GenericButton
			onClick={(): void => cwpStore.toggleFILT()}
			active={cwpStore.showFILT}
		>
			FILT
		</GenericButton>
	);
});

const _CurrentSectorFlightLabelsButton = observer(
	function CurrentSectorFlightLabelsButton() {
		const { currentCWP } = configurationStore;
		if (!currentCWP || currentCWP === "All") {
			return null;
		}
		return (
			<GenericButton
				onClick={(): void => cwpStore.toggleFlightLabelsForCurrentSector()}
				active={cwpStore.showFlightLabelsForCurrentSector}
			>
				{currentCWP} Flight Labels
			</GenericButton>
		);
	},
);

const _OtherSectorsFlightLabelsButton = observer(
	function OtherSectorsFlightLabelsButton() {
		const { currentCWP } = configurationStore;
		if (!currentCWP || currentCWP === "All") {
			return null;
		}
		return (
			<GenericButton
				onClick={(): void => cwpStore.toggleFlightLabelsForOtherSectors()}
				active={cwpStore.showFlightLabelsForOtherSectors}
			>
				Other Flight Labels
			</GenericButton>
		);
	},
);

const OpenVerticalWindowButton = observer(function OpenVerticalWindowButton() {
	const posthog = usePostHog();
	const onClick = (): void => {
		cwpStore.toggleVerticalWindow();
		posthog?.capture("vertical_window_toggled", {
			enabled: !cwpStore.showVerticalWindow,
		});
	};
	return (
		<GenericButton onClick={onClick} active={cwpStore.showVerticalWindow}>
			VAW
		</GenericButton>
	);
});

export default function ImageConfiguration() {
	return (
		<div className="absolute top-0 left-0 h-auto p-0 flex flex-col justify-start items-start z-[1]">
			<div className="flex gap-2 bg-gray-950/50 p-2 ml-2 mt-2 items-center backdrop-blur-[1.5px] rounded-sm">
				<Time />
				<div className="flex">
					<ResetButton />
				</div>
				<div className="flex">
					<FixesButton />
					<AirwaysButton />
					<SectorLabelsButton />
				</div>
				<div className="flex">
					<OpenVerticalWindowButton />
				</div>
				{/* <div>
					<NSFLButton />
				</div>
				<div>
					<N_AButton />
				</div> */}
			</div>
			<div className="flex gap-4 bg-gray-950/50 p-2 ml-2 mt-1.5 items-center backdrop-blur-[1.5px] rounded-sm">
				<SpeedVectorNavbarControl />
			</div>
			{/* <div className="image-configuration-section">
				<FlightLabelsButton />
			</div>
			<div className="image-configuration-section">
				<CurrentSectorFlightLabelsButton />
				<OtherSectorsFlightLabelsButton />
				<LimboFlightsButton />
				<FilterButton />
				<DistanceMeasurementDropdown />
				<ControllerButton />
			</div> */}
		</div>
	);
}
